import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import checkicon from '../../images/checkicon.svg'
import track from '../../lib/track'
import leadAttribution from '../../lib/attribution'
import '../seoCheck/seoCheck.scss'
import './aiSavings.scss'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myzkobag'
const WORK_WEEKS = 46 // a year minus holidays and sick days
const FULL_TIME_HOURS = WORK_WEEKS * 40
const RUNNING_COST = 0.1 // hosting and model usage, as a share of the savings

// share = the part of the work a well-built AI system typically takes over.
const PROCESSES = [
  { id: 'support', share: 50 },
  { id: 'documents', share: 70 },
  { id: 'dataEntry', share: 70 },
  { id: 'sales', share: 40 },
  { id: 'reporting', share: 60 },
  { id: 'other', share: 40 },
]
const CURRENCIES = ['€', '$', '£', '₾']

const money = (n, cur) => `${cur}${Math.round(n).toLocaleString('en-US')}`

/**
 * AI automation savings calculator.
 *
 * Plain arithmetic on the visitor's own numbers: hours a team spends on a
 * process, the share AI can take over, and what an hour costs. Instead of
 * quoting a price it shows the budget that would still pay back within a
 * year — the number a business owner actually decides on. The lead carries
 * every input, so the team can reply with a real proposal.
 */
const AiSavings = () => {
  const { translations: t } = useContext(LanguageContext)
  const navigate = useNavigate()
  const [process, setProcess] = useState('documents')
  const [people, setPeople] = useState(3)
  const [hours, setHours] = useState(15)
  const [rate, setRate] = useState(25)
  const [share, setShare] = useState(70)
  const [currency, setCurrency] = useState('€')
  const [lead, setLead] = useState({ name: '', email: '', company: '', details: '' })
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const pick = (p) => {
    setProcess(p.id)
    setShare(p.share)
  }

  const num = (v) => Math.max(0, Number(v) || 0)
  const hoursNow = num(people) * num(hours) * WORK_WEEKS
  const hoursSaved = hoursNow * (share / 100)
  const gross = hoursSaved * num(rate)
  const net = gross * (1 - RUNNING_COST)
  const fte = hoursSaved / FULL_TIME_HOURS

  const submit = async (event) => {
    event.preventDefault()
    if (status === 'sending') return
    setStatus('sending')
    const summary = [
      `Process: ${t[`aisProc.${process}`]}`,
      `People: ${people}, hours/week each: ${hours}, hourly cost: ${currency}${rate}`,
      `AI share: ${share}%`,
      `Hours saved per year: ${Math.round(hoursSaved)} (${fte.toFixed(1)} FTE)`,
      `Net savings per year: ${money(net, currency)}`,
      lead.details && `\nAbout the process:\n${lead.details}`,
    ]
      .filter(Boolean)
      .join('\n')
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `AI savings lead: ${money(net, currency)}/year on ${t[`aisProc.${process}`]} from ${lead.company || lead.name}`,
          name: lead.name,
          email: lead.email,
          company: lead.company,
          message: summary,
          source: 'AI savings calculator',
          ...leadAttribution(),
        }),
      })
      setStatus(res.ok ? 'sent' : 'error')
      if (res.ok) track('ai_savings_lead', { savings: Math.round(net) })
    } catch (err) {
      setStatus('error')
    }
  }

  const field = (label, value, set, props) => (
    <label className='ais-field'>
      <span>{label}</span>
      <input type='number' inputMode='decimal' min='0' value={value} onChange={(e) => set(e.target.value)} {...props} />
    </label>
  )

  return (
    <Layout>
      <Seo
        title={t.aisSeoTitle}
        description={t.aisSeoDescription}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'AI savings calculator', path: '/ai-savings' },
          ]),
        ]}
      />

      <div className='seoc-page'>
        <div className='seoc-content'>
          <p className='seoc-kicker'>{t.aisKicker}</p>
          <h1 className='seoc-title'>{t.aisTitle}</h1>
          <p className='seoc-sub'>{t.aisSub}</p>

          <div className='ais-card'>
            <h2 className='seoc-block-title'>{t.aisProcessLabel}</h2>
            <div className='ais-chips' role='radiogroup' aria-label={t.aisProcessLabel}>
              {PROCESSES.map((p) => (
                <button
                  key={p.id}
                  type='button'
                  role='radio'
                  aria-checked={process === p.id}
                  className={`ais-chip${process === p.id ? ' is-active' : ''}`}
                  onClick={() => pick(p)}
                >
                  {t[`aisProc.${p.id}`]}
                </button>
              ))}
            </div>
            <p className='seoc-small ais-proc-hint'>{t[`aisProcHint.${process}`]}</p>

            <div className='ais-grid'>
              {field(t.aisPeople, people, setPeople, { max: 10000 })}
              {field(t.aisHours, hours, setHours, { max: 60 })}
              <div className='ais-field'>
                <span>{t.aisRate}</span>
                <div className='ais-money'>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label={t.aisCurrency}>
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input type='number' inputMode='decimal' min='0' value={rate} onChange={(e) => setRate(e.target.value)} aria-label={t.aisRate} />
                </div>
              </div>
            </div>

            <label className='ais-slider'>
              <span>
                {t.aisShare} <strong>{share}%</strong>
              </span>
              <input type='range' min='10' max='90' step='5' value={share} onChange={(e) => setShare(Number(e.target.value))} />
            </label>
          </div>

          <div className='ais-results' aria-live='polite'>
            <div className='ais-result ais-result-main'>
              <span className='ais-result-value'>{money(net, currency)}</span>
              <span className='ais-result-label'>{t.aisNet}</span>
            </div>
            <div className='ais-result'>
              <span className='ais-result-value'>{Math.round(hoursSaved).toLocaleString('en-US')}</span>
              <span className='ais-result-label'>{t.aisHoursSaved}</span>
            </div>
            <div className='ais-result'>
              <span className='ais-result-value'>{fte.toFixed(1)}</span>
              <span className='ais-result-label'>{t.aisFte}</span>
            </div>
          </div>

          <div className='seoc-top ais-budget'>
            <p>{t.aisBudgetLead}</p>
            <strong>{money(net, currency)}</strong>
            <p className='seoc-small'>{t.aisBudgetNote}</p>
            <p className='seoc-small'>{t.aisAssumptions}</p>
          </div>

          <div className='seoc-lead'>
            {status === 'sent' ? (
              <div className='seoc-sent'>
                <img src={checkicon} alt='' width='40' height='40' />
                <div>
                  <h3>{t.seocSentTitle}</h3>
                  <p>{t.aisSentText}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h3 className='seoc-lead-title'>{t.aisLeadTitle}</h3>
                <p className='seoc-lead-sub'>{t.aisLeadSub}</p>
                <div className='seoc-lead-row'>
                  <label>
                    <span>{t.quoteName}</span>
                    <input type='text' value={lead.name} autoComplete='name' required onChange={(e) => setLead({ ...lead, name: e.target.value })} />
                  </label>
                  <label>
                    <span>{t.quoteEmail}</span>
                    <input type='email' value={lead.email} autoComplete='email' required onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                  </label>
                </div>
                <div className='seoc-lead-row'>
                  <label>
                    <span>{t.readyCompany}</span>
                    <input type='text' value={lead.company} autoComplete='organization' onChange={(e) => setLead({ ...lead, company: e.target.value })} />
                  </label>
                </div>
                <label className='ais-details'>
                  <span>{t.aisDetails}</span>
                  <textarea rows='3' value={lead.details} onChange={(e) => setLead({ ...lead, details: e.target.value })} />
                </label>
                <button type='submit' className='seoc-lead-btn' disabled={status === 'sending'}>
                  {status === 'sending' ? t.quoteSending : t.aisLeadBtn}
                </button>
                {status === 'error' && <p className='seoc-error'>{t['seocErr.default']}</p>}
                <p className='seoc-lead-note'>{t.quoteNote}</p>
              </form>
            )}
            <div className='seoc-lead-links'>
              <button type='button' onClick={() => navigate('/ai-readiness')}>{t.aisReadyLink} →</button>
              <button type='button' onClick={() => navigate('/estimate')}>{t.aisEstimateLink} →</button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default AiSavings
